import { useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, Flag, MapPin, Pencil, Phone, Send } from 'lucide-react';

import { isApiError } from '@starling/core/api/errors';
import { formatDate, formatPhone, formatPlace } from '@/lib/format';
import { whatsappUrl } from '@/lib/whatsapp';
import { useSession } from '@/auth/hooks';
import { useMyOrganizationsQuery } from '@/organization/hooks';
import { FormError, GhostButton, PrimaryButton, TextAreaField } from '@/components/form/primitives';
import {
  categoryLabel,
  useCategoriesQuery,
  useListingQuery,
  useReportListingMutation,
} from './hooks';
import styles from './ListingDetailPage.module.css';

// Детали объявления — отдельный экран (не модалка): ссылку на объявление можно
// переслать коллеге, а вкладки/поиск ленты на этом экране не нужны.
// Вёрстка собрана из примитивов прототипа (карточка, тег-пилюля, мятный блок).

/**
 * «Пожаловаться» (V1.3-c): кнопка → инлайн-поле причины (≤500) → POST report.
 * Экрана в прототипе нет — собрано из его примитивов (ghost-кнопка, FieldStub
 * tall, PrimaryBtn), lightweight-путь design-workflow. Ответ бэка для
 * active-объявления всегда 200 (VD7) — «спасибо» показываем безусловно.
 */
function ReportSection({ listingId }: { listingId: string }): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const mutation = useReportListingMutation(listingId);

  if (mutation.isSuccess) {
    return (
      <p className={styles.reportThanks} role="status">
        {t('report.thanks')}
      </p>
    );
  }

  if (!open) {
    return (
      <div className={styles.reportRow}>
        <button type="button" className={styles.reportButton} onClick={() => setOpen(true)}>
          <Flag size={12} aria-hidden="true" />
          {t('report.button')}
        </button>
      </div>
    );
  }

  return (
    <section className={styles.reportCard} aria-label={t('report.label')}>
      <FormError>{error}</FormError>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          mutation.mutate(
            { reason: reason.trim() },
            { onError: () => setError(t('report.error')) },
          );
        }}
      >
        <TextAreaField
          label={t('report.reason')}
          value={reason}
          onChange={setReason}
          name="reason"
          placeholder={t('report.reasonPlaceholder')}
          maxLength={500}
        />
        <PrimaryButton disabled={mutation.isPending || !reason.trim()}>
          {t('report.submit')}
        </PrimaryButton>
        <div className={styles.reportActions}>
          <GhostButton disabled={mutation.isPending} onClick={() => setOpen(false)}>
            {t('report.cancel')}
          </GhostButton>
        </div>
      </form>
    </section>
  );
}

/** State перехода на карточку: `fromFeed` ставит ListingCard — тогда «К ленте» идёт по истории. */
export interface ListingDetailLocationState {
  fromFeed?: boolean;
}

export function ListingDetailPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const listingQuery = useListingQuery(id);
  const categories = useCategoriesQuery().data?.items;
  // «Своё» объявление: организация карточки ∈ мои организации (запрос
  // включён только при сессии) → вместо «Пожаловаться» — «Редактировать»
  // прямо с карточки, без захода в «Мои объявления» (приёмка AT9, №17).
  const { isAuthenticated } = useSession();
  const myOrganizations = useMyOrganizationsQuery().data?.items;

  // Если на экран пришли из ленты (ListingCard кладёт `fromFeed` в state) —
  // возвращаемся туда же по истории (с фильтрами); иначе — явная ссылка на
  // главную. Раньше критерием было «не прямая ссылка» (`location.key`), и с
  // финала публикации «К ленте» уводила назад в форму /post (приёмка AT8).
  const cameFromFeed = (location.state as ListingDetailLocationState | null)?.fromFeed === true;
  const backLink = cameFromFeed ? (
    <button type="button" className={styles.back} onClick={() => navigate(-1)}>
      <ChevronLeft size={14} aria-hidden="true" />
      {t('listing.back')}
    </button>
  ) : (
    <Link to="/" className={styles.back}>
      <ChevronLeft size={14} aria-hidden="true" />
      {t('listing.back')}
    </Link>
  );

  if (listingQuery.isPending) {
    return (
      <div>
        {backLink}
        <div className={styles.state}>{t('listing.loading')}</div>
      </div>
    );
  }

  if (listingQuery.isError) {
    // 404 — объявление снято/archived/не существует (VD6: единый listing_not_found).
    const notFound = isApiError(listingQuery.error) && listingQuery.error.status === 404;
    return (
      <div>
        {backLink}
        <div className={`${styles.state} ${notFound ? '' : styles.stateError}`}>
          {notFound ? t('listing.notFound') : t('listing.error')}
        </div>
      </div>
    );
  }

  const listing = listingQuery.data;
  const isMine =
    isAuthenticated && (myOrganizations ?? []).some((org) => org.id === listing.organization_id);

  return (
    <div>
      {backLink}
      <article className={styles.card}>
        <div className={styles.top}>
          <h1 className={styles.title}>{listing.title}</h1>
          <span className={styles.tag}>
            {categoryLabel(categories, listing.category_id, i18n.language)}
          </span>
        </div>

        <div className={styles.meta}>
          <MapPin size={12} aria-hidden="true" />
          {formatPlace(listing.city, listing.district)} · {listing.organization_name}
        </div>
        <div className={styles.meta}>
          {t('listing.published')}: {formatDate(listing.published_at, i18n.language)}
        </div>

        <p className={styles.description}>{listing.description}</p>

        <div className={styles.contactBox}>
          <div className={styles.contactLabel}>{t('listing.contact')}</div>
          <div className={styles.contactRow}>
            {listing.contact_phone &&
              (listing.contact_whatsapp ? (
                // №22: телефон с WhatsApp — вся строка ведёт в чат wa.me с
                // приветствием по объявлению; бейдж — «зелёный кружок с трубкой».
                <a
                  className={styles.contactItem}
                  href={whatsappUrl(
                    listing.contact_phone,
                    t('listing.whatsappText', { title: listing.title }),
                  )}
                  target="_blank"
                  rel="noreferrer noopener"
                  title={t('listing.whatsapp')}
                >
                  <span
                    className={`${styles.contactBadge} ${styles.badgeWhatsapp}`}
                    aria-hidden="true"
                  >
                    <Phone size={10} strokeWidth={2.5} />
                  </span>
                  {formatPhone(listing.contact_phone)}
                </a>
              ) : (
                <span className={styles.contactItem}>{formatPhone(listing.contact_phone)}</span>
              ))}
            {listing.contact_telegram && (
              <a
                className={styles.contactItem}
                href={`https://t.me/${listing.contact_telegram}`}
                target="_blank"
                rel="noreferrer noopener"
                title={t('listing.telegram')}
              >
                <span
                  className={`${styles.contactBadge} ${styles.badgeTelegram}`}
                  aria-hidden="true"
                >
                  <Send size={10} strokeWidth={2.5} />
                </span>
                @{listing.contact_telegram}
              </a>
            )}
          </div>
        </div>

        {listing.geo_ref && (
          <a
            className={styles.geo}
            href={listing.geo_ref}
            target="_blank"
            rel="noreferrer noopener"
          >
            <MapPin size={12} aria-hidden="true" />
            {t('listing.geo')}
          </a>
        )}
      </article>

      {isMine ? (
        <div className={styles.reportRow}>
          <Link
            to={`/my/listings/${listing.id}/edit`}
            state={{ listing, returnTo: location.pathname }}
            className={styles.editLink}
          >
            <Pencil size={13} aria-hidden="true" />
            {t('listing.edit')}
          </Link>
        </div>
      ) : (
        <ReportSection listingId={listing.id} />
      )}
    </div>
  );
}
